import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { Controller } from "../lifecycle";

gsap.registerPlugin(ScrollTrigger);

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export class ScrollFilmController implements Controller {
  private container?: HTMLElement;
  private story?: HTMLElement;
  private video?: HTMLVideoElement;
  private state?: HTMLElement;
  private trigger?: ScrollTrigger;
  private duration = 0;
  private targetTime = 0;
  private displayedTime = 0;
  private frameRequest = 0;
  private videoFrameRequest = 0;
  private decoderReleaseTimer = 0;
  private lastTick = 0;
  private lastSeek = 0;
  private seekPending = false;
  private mounted = false;

  private readonly onMetadata = (): void => {
    if (!this.video || !Number.isFinite(this.video.duration)) return;
    this.duration = this.video.duration;
    this.targetTime = 0.001;
    this.displayedTime = 0.001;
    this.video.currentTime = 0.001;
    this.video.pause();
    this.container?.classList.add("scroll-film--ready");
    if (this.state) {
      this.state.textContent = "Film ready. Scroll to travel through the pattern.";
      window.setTimeout(() => this.state?.setAttribute("hidden", ""), 900);
    }
    ScrollTrigger.refresh();
  };

  private readonly onVideoError = (): void => {
    this.container?.classList.add("scroll-film--error");
    if (this.state) {
      this.state.removeAttribute("hidden");
      this.state.textContent =
        "The film could not be loaded. The complete chapter narrative remains available.";
    }
  };

  private readonly onSeeked = (): void => {
    this.releaseSeekLock();
  };

  mount(root: ParentNode): void {
    this.container = root.querySelector<HTMLElement>("[data-scroll-film]") ?? undefined;
    this.story = root.querySelector<HTMLElement>("[data-film-story]") ?? undefined;
    this.video = root.querySelector<HTMLVideoElement>("[data-film-video]") ?? undefined;
    this.state = root.querySelector<HTMLElement>("[data-film-state]") ?? undefined;

    if (!this.container || !this.story || !this.video) return;

    this.mounted = true;
    this.video.addEventListener("loadedmetadata", this.onMetadata);
    this.video.addEventListener("error", this.onVideoError);
    this.video.addEventListener("seeked", this.onSeeked);

    this.trigger = ScrollTrigger.create({
      trigger: this.story,
      start: "top top",
      end: "bottom bottom",
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        if (this.duration <= 0) return;
        this.targetTime = clamp(self.progress, 0, 1) * Math.max(0.001, this.duration - 0.035);
      }
    });

    if (this.video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      this.onMetadata();
    } else {
      this.video.load();
    }

    this.lastTick = performance.now();
    this.frameRequest = requestAnimationFrame(this.tick);
  }

  private readonly tick = (now: number): void => {
    if (!this.mounted || !this.video) return;

    const deltaSeconds = clamp((now - this.lastTick) / 1000, 0, 0.1);
    this.lastTick = now;

    if (this.duration > 0) {
      const damping = 12;
      const alpha = 1 - Math.exp(-damping * deltaSeconds);
      this.displayedTime += (this.targetTime - this.displayedTime) * alpha;

      const seekDelta = Math.abs(this.video.currentTime - this.displayedTime);
      const enoughTimePassed = now - this.lastSeek >= 32;

      if (
        seekDelta > 0.012 &&
        enoughTimePassed &&
        !this.seekPending &&
        !this.video.seeking &&
        this.video.readyState >= HTMLMediaElement.HAVE_METADATA
      ) {
        this.seekPending = true;
        this.lastSeek = now;
        this.video.currentTime = clamp(
          this.displayedTime,
          0.001,
          Math.max(0.001, this.duration - 0.035)
        );
        this.releaseDecoder();
      }
    }

    this.frameRequest = requestAnimationFrame(this.tick);
  };

  private releaseDecoder(): void {
    if (!this.video) return;
    const video = this.video as HTMLVideoElement & {
      requestVideoFrameCallback?: (
        callback: (now: DOMHighResTimeStamp, metadata: unknown) => void
      ) => number;
    };

    if (typeof video.requestVideoFrameCallback === "function") {
      this.videoFrameRequest = video.requestVideoFrameCallback(() => {
        this.releaseSeekLock();
      });
    }

    window.clearTimeout(this.decoderReleaseTimer);
    this.decoderReleaseTimer = window.setTimeout(() => {
      this.releaseSeekLock();
    }, 800);
  }

  private releaseSeekLock(): void {
    this.seekPending = false;
    window.clearTimeout(this.decoderReleaseTimer);
    this.decoderReleaseTimer = 0;
  }

  destroy(): void {
    this.mounted = false;
    this.trigger?.kill();
    this.trigger = undefined;
    cancelAnimationFrame(this.frameRequest);

    if (this.video && this.videoFrameRequest) {
      const video = this.video as HTMLVideoElement & {
        cancelVideoFrameCallback?: (handle: number) => void;
      };
      video.cancelVideoFrameCallback?.(this.videoFrameRequest);
    }

    this.video?.removeEventListener("loadedmetadata", this.onMetadata);
    this.video?.removeEventListener("error", this.onVideoError);
    this.video?.removeEventListener("seeked", this.onSeeked);
    this.video?.pause();
    window.clearTimeout(this.decoderReleaseTimer);
    this.container = undefined;
    this.story = undefined;
    this.video = undefined;
    this.state = undefined;
    this.duration = 0;
    this.targetTime = 0;
    this.displayedTime = 0;
    this.seekPending = false;
    this.decoderReleaseTimer = 0;
  }
}
